import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import usePageMeta from '@/hooks/usePageMeta';

interface Section {
  id: string;
  title: string;
  content: React.ReactNode;
}

const sections: Section[] = [
  {
    id: 'verantwortlicher',
    title: '1. Verantwortlicher',
    content: (
      <div className="text-sm text-gray-700 leading-relaxed">
        <p className="mb-3">Verantwortlicher im Sinne der Datenschutz-Grundverordnung (DSGVO) ist:</p>
        <address className="not-italic bg-gray-50 rounded-lg p-4 border border-gray-100">
          <strong className="text-gray-900">gowiTEC</strong><br />
          Inhaber: Hans-Jörg Gollan<br />
          Gewerbeparkstr. 8<br />
          51580 Reichshof-Wehnrath<br />
          Deutschland<br /><br />
          Telefon: <a href="tel:+491776512122" className="text-[#1E3A8A] hover:underline">+49 1776512122</a><br />
          E-Mail: <a href="mailto:anfrage@gowitec.de" className="text-[#1E3A8A] hover:underline">anfrage@gowitec.de</a>
        </address>
      </div>
    ),
  },
  {
    id: 'grundsaetze',
    title: '2. Allgemeines zur Datenverarbeitung',
    content: (
      <div className="text-sm text-gray-700 leading-relaxed flex flex-col gap-3">
        <p>
          Der Schutz Ihrer personenbezogenen Daten ist uns wichtig. Wir verarbeiten personenbezogene Daten nur im Rahmen der geltenden datenschutzrechtlichen Vorschriften, insbesondere der Datenschutz-Grundverordnung (DSGVO) und des Bundesdatenschutzgesetzes (BDSG).
        </p>
        <p>
          Personenbezogene Daten sind alle Informationen, die sich auf eine identifizierte oder identifizierbare natürliche Person beziehen. Wir erheben, verarbeiten und nutzen Ihre personenbezogenen Daten nur, soweit dies zur Bereitstellung unserer Leistungen erforderlich ist oder Sie uns Ihre Einwilligung erteilt haben.
        </p>
      </div>
    ),
  },
  {
    id: 'hosting',
    title: '3. Hosting & technischer Betrieb',
    content: (
      <div className="text-sm text-gray-700 leading-relaxed flex flex-col gap-3">
        <p>
          Diese Website wird über <strong className="text-gray-900">STRATO AG</strong> (Pascalstraße 10, 10587 Berlin) gehostet. Beim Aufruf dieser Website werden durch den Hosting-Anbieter technisch erforderliche Daten verarbeitet, insbesondere:
        </p>
        <ul className="flex flex-col gap-1.5 pl-1">
          {[
            'IP-Adresse des anfragenden Rechners',
            'Datum und Uhrzeit des Zugriffs',
            'Name und URL der abgerufenen Datei',
            'Browsertyp und Betriebssystem',
            'Referrer-URL (zuvor besuchte Seite)',
          ].map((item) => (
            <li key={item} className="flex items-center gap-2 text-gray-600">
              <span className="w-4 h-4 flex items-center justify-center shrink-0">
                <i className="ri-arrow-right-s-line text-[#1E3A8A]"></i>
              </span>
              {item}
            </li>
          ))}
        </ul>
        <p>
          <strong className="text-gray-900">Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der technischen Bereitstellung und Sicherheit der Website).
        </p>
        <p>
          <strong className="text-gray-900">Speicherdauer:</strong> Server-Logfiles werden in der Regel nach 7 Tagen automatisch gelöscht, sofern keine sicherheitsrelevanten Ereignisse eine längere Speicherung erfordern.
        </p>
        <p>
          Weitere Informationen zum Datenschutz bei STRATO finden Sie unter:{' '}
          <a href="https://www.strato.de/datenschutz/" target="_blank" rel="noopener noreferrer nofollow" className="text-[#1E3A8A] hover:underline">www.strato.de/datenschutz</a>
        </p>
      </div>
    ),
  },
  {
    id: 'kontaktformular',
    title: '4. Kontaktaufnahme & Kontaktformular',
    content: (
      <div className="text-sm text-gray-700 leading-relaxed flex flex-col gap-4">
        <p>
          Wenn Sie uns per E-Mail oder über das Kontaktformular auf unserer Website kontaktieren, verarbeiten wir die von Ihnen übermittelten personenbezogenen Daten (z. B. Name, E-Mail-Adresse, Telefonnummer, Nachrichteninhalt) ausschließlich zur Bearbeitung Ihrer Anfrage und für den Fall von Anschlussfragen.
        </p>
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 flex flex-col gap-2">
          <p className="font-semibold text-gray-900">Verarbeitete Daten:</p>
          <ul className="flex flex-col gap-1.5 pl-1">
            {['Name', 'E-Mail-Adresse', 'Telefonnummer (optional)', 'Unternehmen (optional)', 'Nachrichteninhalt'].map((item) => (
              <li key={item} className="flex items-center gap-2 text-gray-600">
                <span className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-arrow-right-s-line text-[#1E3A8A]"></i>
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>
        <p>
          <strong className="text-gray-900">Formulardienst:</strong> Die Übermittlung der Formulardaten erfolgt direkt über unseren eigenen Webserver (STRATO AG) mittels PHP. Die eingegebenen Daten werden ausschließlich per E-Mail an uns weitergeleitet und nicht dauerhaft auf dem Server gespeichert.
        </p>
        <p>
          <strong className="text-gray-900">Rechtsgrundlage:</strong> Die Verarbeitung erfolgt gemäß Art. 6 Abs. 1 lit. b DSGVO zur Durchführung vorvertraglicher Maßnahmen sowie gemäß Art. 6 Abs. 1 lit. f DSGVO auf Basis unseres berechtigten Interesses an der Bearbeitung von Kundenanfragen.
        </p>
        <p>
          <strong className="text-gray-900">Speicherdauer:</strong> Die Daten werden gelöscht, sobald sie für die Erreichung des Zwecks ihrer Erhebung nicht mehr erforderlich sind. Dies ist in der Regel dann der Fall, wenn die jeweilige Konversation mit Ihnen beendet ist und keine gesetzlichen Aufbewahrungspflichten entgegenstehen.
        </p>
        <p>
          <strong className="text-gray-900">Weitergabe:</strong> Eine Weitergabe Ihrer Daten an Dritte findet nicht statt, sofern dies nicht zur Bearbeitung Ihrer Anfrage erforderlich ist oder Sie ausdrücklich eingewilligt haben.
        </p>
        <p>
          Sie haben jederzeit das Recht, der Verarbeitung Ihrer personenbezogenen Daten zu widersprechen. Wenden Sie sich dazu bitte an:{' '}
          <a href="mailto:anfrage@gowitec.de" className="text-[#1E3A8A] hover:underline">anfrage@gowitec.de</a>
        </p>
      </div>
    ),
  },
  {
    id: 'bewerbung',
    title: '5. Bewerbungsformular',
    content: (
      <div className="text-sm text-gray-700 leading-relaxed flex flex-col gap-4">
        <p>
          Auf unserer Stellenangebote-Seite bieten wir ein Online-Bewerbungsformular an. Die dort eingegebenen personenbezogenen Daten werden ausschließlich zur Bearbeitung Ihrer Bewerbung verwendet.
        </p>
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 flex flex-col gap-2">
          <p className="font-semibold text-gray-900">Verarbeitete Daten:</p>
          <ul className="flex flex-col gap-1.5 pl-1">
            {['Name', 'E-Mail-Adresse', 'Telefonnummer (optional)', 'Ausbildung / Qualifikation', 'Motivationsschreiben / Vorstellung', 'Gewünschte Stelle'].map((item) => (
              <li key={item} className="flex items-center gap-2 text-gray-600">
                <span className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-arrow-right-s-line text-[#1E3A8A]"></i>
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>
        <p>
          <strong className="text-gray-900">Formulardienst:</strong> Die Übermittlung der Bewerbungsdaten erfolgt direkt über unseren eigenen Webserver (STRATO AG) mittels PHP. Die eingegebenen Daten werden ausschließlich per E-Mail an uns weitergeleitet und nicht dauerhaft auf dem Server gespeichert.
        </p>
        <p>
          <strong className="text-gray-900">Rechtsgrundlage:</strong> Die Verarbeitung erfolgt gemäß Art. 6 Abs. 1 lit. b DSGVO zur Durchführung vorvertraglicher Maßnahmen (Anbahnung eines Beschäftigungsverhältnisses) sowie § 26 BDSG.
        </p>
        <p>
          <strong className="text-gray-900">Speicherdauer:</strong> Bewerbungsunterlagen werden nach Abschluss des Bewerbungsverfahrens gelöscht, spätestens nach 6 Monaten, sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen oder Sie einer längeren Speicherung zugestimmt haben.
        </p>
        <p>
          <strong className="text-gray-900">Weitergabe:</strong> Ihre Bewerbungsdaten werden nicht an Dritte weitergegeben. Die Verarbeitung erfolgt ausschließlich intern durch die zuständigen Personen im Unternehmen.
        </p>
        <p>
          Sie können Ihre Einwilligung jederzeit widerrufen und die Löschung Ihrer Bewerbungsdaten verlangen. Wenden Sie sich dazu an:{' '}
          <a href="mailto:anfrage@gowitec.de" className="text-[#1E3A8A] hover:underline">anfrage@gowitec.de</a>
        </p>
      </div>
    ),
  },
  {
    id: 'google-maps',
    title: '6. Google Maps',
    content: (
      <div className="text-sm text-gray-700 leading-relaxed flex flex-col gap-3">
        <p>
          Auf unserer Kontaktseite ist eine Karte von Google Maps eingebunden. <strong className="text-gray-900">Die Karte wird erst nach Ihrer ausdrücklichen Zustimmung (Klick auf „Karte laden &amp; zustimmen") geladen</strong> – bis dahin werden keine Daten an Google übertragen.
        </p>
        <p>
          Nach Ihrer Zustimmung wird eine Verbindung zu Servern von Google hergestellt. Dabei können personenbezogene Daten, insbesondere Ihre IP-Adresse, an Google übermittelt werden.
        </p>
        <p>
          <strong className="text-gray-900">Anbieter:</strong> Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland (Muttergesellschaft: Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA).
        </p>
        <p>
          <strong className="text-gray-900">Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung durch aktiven Klick).
        </p>
        <p>
          Weitere Informationen zur Datenverarbeitung durch Google finden Sie in den{' '}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="text-[#1E3A8A] hover:underline"
          >
            Datenschutzhinweisen von Google
          </a>.
        </p>
      </div>
    ),
  },
  {
    id: 'cookies',
    title: '7. Cookies & Tracking',
    content: (
      <div className="text-sm text-gray-700 leading-relaxed flex flex-col gap-3">
        <p>
          Diese Website verwendet <strong className="text-gray-900">keine Tracking-, Analyse- oder Marketing-Cookies</strong>. Es werden insbesondere <strong className="text-gray-900">kein Google Analytics, kein Facebook Pixel und keine vergleichbaren Tracking-Dienste</strong> eingesetzt.
        </p>
        <p>
          Es werden ausschließlich technisch notwendige Funktionen genutzt, die für den Betrieb der Website erforderlich sind. Eine Einwilligung nach § 25 TTDSG ist daher für den regulären Seitenbesuch nicht erforderlich.
        </p>
        <p>
          Wenn Sie der Einbindung von Google Maps zustimmen (Abschnitt 6), können durch Google LLC Cookies gesetzt werden. Weitere Informationen hierzu finden Sie in der{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer nofollow" className="text-[#1E3A8A] hover:underline">
            Datenschutzerklärung von Google
          </a>.
        </p>
      </div>
    ),
  },
  {
    id: 'keine-analyse',
    title: '8. Keine Analyse- oder Werbedienste',
    content: (
      <div className="text-sm text-gray-700 leading-relaxed flex flex-col gap-3">
        <p>
          Auf dieser Website werden <strong className="text-gray-900">keine Webanalyse-Dienste</strong> (z. B. Google Analytics, Matomo, Hotjar) und <strong className="text-gray-900">keine Social-Media-Plugins</strong> (z. B. Facebook, Instagram, LinkedIn) eingesetzt, die eine Verfolgung Ihres Nutzerverhaltens ermöglichen würden.
        </p>
        <p>
          Es findet keine Erstellung von Nutzerprofilen und keine Weitergabe von Besucherdaten an Werbenetzwerke statt.
        </p>
      </div>
    ),
  },
  {
    id: 'rechte',
    title: '9. Ihre Rechte als betroffene Person',
    content: (
      <div className="text-sm text-gray-700 leading-relaxed">
        <p className="mb-4">Nach der DSGVO stehen Ihnen folgende Rechte zu:</p>
        <div className="flex flex-col gap-3 mb-4">
          {[
            { icon: 'ri-eye-line', title: 'Auskunftsrecht (Art. 15 DSGVO)', desc: 'Sie haben das Recht, eine Bestätigung darüber zu verlangen, ob Sie betreffende personenbezogene Daten verarbeitet werden, sowie Auskunft über diese Daten.' },
            { icon: 'ri-edit-line', title: 'Recht auf Berichtigung (Art. 16 DSGVO)', desc: 'Sie haben das Recht, unverzüglich die Berichtigung unrichtiger personenbezogener Daten zu verlangen.' },
            { icon: 'ri-delete-bin-line', title: 'Recht auf Löschung (Art. 17 DSGVO)', desc: 'Sie haben das Recht, die unverzügliche Löschung der Sie betreffenden personenbezogenen Daten zu verlangen, sofern die gesetzlichen Voraussetzungen vorliegen.' },
            { icon: 'ri-pause-circle-line', title: 'Recht auf Einschränkung (Art. 18 DSGVO)', desc: 'Sie haben das Recht, die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen.' },
            { icon: 'ri-download-line', title: 'Recht auf Datenübertragbarkeit (Art. 20 DSGVO)', desc: 'Sie haben das Recht, die Sie betreffenden personenbezogenen Daten in einem strukturierten, gängigen und maschinenlesbaren Format zu erhalten.' },
            { icon: 'ri-close-circle-line', title: 'Widerspruchsrecht (Art. 21 DSGVO)', desc: 'Sie haben das Recht, aus Gründen, die sich aus Ihrer besonderen Situation ergeben, jederzeit gegen die Verarbeitung Widerspruch einzulegen.' },
            { icon: 'ri-shield-check-line', title: 'Widerrufsrecht bei Einwilligung (Art. 7 Abs. 3 DSGVO)', desc: 'Sofern die Verarbeitung auf einer Einwilligung beruht, haben Sie das Recht, diese jederzeit mit Wirkung für die Zukunft zu widerrufen.' },
          ].map((right) => (
            <div key={right.title} className="flex items-start gap-3 bg-gray-50 rounded-lg p-4 border border-gray-100">
              <span className="w-8 h-8 flex items-center justify-center bg-[#1E3A8A]/10 rounded-lg shrink-0">
                <i className={`${right.icon} text-[#1E3A8A]`}></i>
              </span>
              <div>
                <p className="font-semibold text-gray-900 text-sm mb-1">{right.title}</p>
                <p className="text-gray-600 text-xs leading-relaxed">{right.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <p>
          Zur Ausübung Ihrer Rechte wenden Sie sich bitte an:{' '}
          <a href="mailto:anfrage@gowitec.de" className="text-[#1E3A8A] hover:underline">anfrage@gowitec.de</a>
        </p>
      </div>
    ),
  },
  {
    id: 'aufsichtsbehoerde',
    title: '10. Beschwerderecht bei der Aufsichtsbehörde',
    content: (
      <div className="text-sm text-gray-700 leading-relaxed flex flex-col gap-3">
        <p>
          Gemäß Art. 77 DSGVO haben Sie das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren, wenn Sie der Ansicht sind, dass die Verarbeitung der Sie betreffenden personenbezogenen Daten gegen die DSGVO verstößt.
        </p>
        <p>
          Die zuständige Aufsichtsbehörde für gowiTEC ist:
        </p>
        <address className="not-italic bg-gray-50 rounded-lg p-4 border border-gray-100">
          <strong className="text-gray-900">Landesbeauftragte für Datenschutz und Informationsfreiheit Nordrhein-Westfalen (LDI NRW)</strong><br />
          Kavalleriestraße 2–4<br />
          40213 Düsseldorf<br /><br />
          Telefon: +49 211 38424-0<br />
          E-Mail: <a href="mailto:poststelle@ldi.nrw.de" className="text-[#1E3A8A] hover:underline">poststelle@ldi.nrw.de</a><br />
          Website: <a href="https://www.ldi.nrw.de" target="_blank" rel="noopener noreferrer nofollow" className="text-[#1E3A8A] hover:underline">www.ldi.nrw.de</a>
        </address>
        <p>
          Dieses Recht besteht unbeschadet anderweitiger verwaltungsrechtlicher oder gerichtlicher Rechtsbehelfe.
        </p>
      </div>
    ),
  },
  {
    id: 'aktualitaet',
    title: '11. Aktualität & Änderungen',
    content: (
      <p className="text-sm text-gray-700 leading-relaxed">
        Wir behalten uns vor, diese Datenschutzerklärung bei Bedarf anzupassen, um sie stets den aktuellen rechtlichen Anforderungen entsprechend zu halten oder um Änderungen unserer Leistungen umzusetzen. Die jeweils aktuelle Version gilt für Ihren nächsten Besuch. <strong className="text-gray-900">Stand: April 2026.</strong>
      </p>
    ),
  },
];

const DatenschutzPage = () => {
  usePageMeta({
    title: 'Datenschutzerklärung | gowiTEC – Sondermaschinenbau Reichshof',
    description: 'Datenschutzerklärung von gowiTEC gemäß DSGVO. Informationen zur Verarbeitung personenbezogener Daten, Kontaktformular, Bewerbungsformular und Google Maps.',
    canonical: 'https://gowitec.de/datenschutz',
    noindex: true,
  });

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      {/* Header */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-[#f8fafc] border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#1E3A8A] transition-colors cursor-pointer mb-6"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </span>
            Zurück zur Startseite
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Datenschutzerklärung</h1>
          <p className="text-gray-500 text-sm">Gemäß DSGVO (EU) 2016/679 – Stand: April 2026</p>
        </div>
      </section>

      {/* Content */}
      <main className="flex-1 py-14 md:py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-10">

          {/* Inhaltsverzeichnis */}
          <nav className="bg-[#f8fafc] border border-gray-100 rounded-xl p-6 mb-12" aria-label="Inhaltsverzeichnis">
            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-[#1E3A8A]">
                <i className="ri-list-check-2"></i>
              </span>
              Inhaltsverzeichnis
            </h2>
            <ol className="flex flex-col gap-2">
              {sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-sm text-[#1E3A8A] hover:underline cursor-pointer"
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {/* Sections */}
          <div className="flex flex-col gap-10">
            {sections.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-28">
                <h4 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                  <a href={`#${s.id}`} className="hover:text-[#1E3A8A] transition-colors">{s.title}</a>
                </h4>
                {s.content}
              </section>
            ))}
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DatenschutzPage;
